import type dayjs from "dayjs";

import { Button, Col, DatePicker, Row } from "antd";
import { type Dispatch, type SetStateAction, useId } from "react";
import { useTranslation } from "react-i18next";

import { theme } from "design-system/config/theme";

import type { FiltersProps } from "../type";

import s from "./Filters.module.css";
import { useAuditLogFilterOptions } from "./hooks";
import { SelectFilter } from "./SelectFilter";

const { RangePicker } = DatePicker;

interface Props {
  filters: FiltersProps;
  handleSearch?: () => void;
  onFiltersChange: Dispatch<SetStateAction<FiltersProps>>;
  resetFilters?: () => void;
}

export const Filters = ({
  filters,
  onFiltersChange,
  resetFilters,
  handleSearch,
}: Props) => {
  const { t } = useTranslation(["common"]);
  const { name, email, section, region, fromDate, toDate } = filters;
  const nameId = useId();
  const emailId = useId();
  const sectionId = useId();
  const regionId = useId();
  const rangeId = useId();
  const { components } = theme;

  const filterOptions = useAuditLogFilterOptions();

  const handleChange = (key: keyof FiltersProps, value: any) => {
    onFiltersChange(prev => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleRangeChange = (
    dates: [dayjs.Dayjs | null, dayjs.Dayjs | null] | null,
  ) => {
    if (!dates?.length) {
      handleChange("fromDate", null);
      handleChange("toDate", null);
      return;
    }

    const [start, end] = dates;

    handleChange("fromDate", start);
    handleChange("toDate", end);
  };

  return (
    <section
      className={s.container}
      style={{ backgroundColor: components?.Table?.headerBg }}
    >
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} md={8}>
          <SelectFilter
            id={nameId}
            field="name"
            label={t("labels.nameLabel")}
            value={name}
            handleChange={handleChange}
            filterOptions={filterOptions}
            placeholder={`${t("labels.all")} ${t("labels.nameLabel")}`}
          />
        </Col>

        <Col xs={24} sm={12} md={8}>
          <SelectFilter
            id={emailId}
            field="email"
            label={t("labels.email")}
            value={email}
            handleChange={handleChange}
            filterOptions={filterOptions}
            placeholder={`${t("labels.all")} ${t("labels.email")}`}
          />
        </Col>

        <Col xs={24} sm={12} md={8}>
          <SelectFilter
            id={sectionId}
            field="section"
            label={t("components.auditLog.section")}
            value={section}
            handleChange={handleChange}
            filterOptions={filterOptions}
            placeholder={`${t("labels.all")} ${t("components.auditLog.section")}`}
          />
        </Col>

        <Col xs={24} sm={12} md={12}>
          <SelectFilter
            id={regionId}
            field="region"
            label={t("labels.region")}
            value={region}
            handleChange={handleChange}
            filterOptions={filterOptions}
            placeholder={`${t("labels.all")} ${t("labels.region")}`}
          />
        </Col>

        <Col xs={24} sm={12} md={12}>
          <div className={s.field}>
            <label htmlFor={rangeId}>{t("labels.date")}</label>

            <RangePicker
              id={rangeId}
              style={{ width: "100%" }}
              value={[fromDate || null, toDate || null]}
              onChange={handleRangeChange}
              allowClear
            />
          </div>
        </Col>
      </Row>

      <Row justify="end" className={s.actions}>
        <Button
          type="default"
          className={s.clearSearch}
          onClick={resetFilters}
          data-testid="clear-search-button"
        >
          {t("components.buttons.clearSearch")}
        </Button>
        <Button
          type="primary"
          onClick={handleSearch}
          data-testid="search-button"
        >
          {t("labels.search")}
        </Button>
      </Row>
    </section>
  );
};
